import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMutation } from '@apollo/client';
import { Alert, Card, CardContent, Typography } from '@guildeducationinc/recess';
import { UploadDocuments } from '@src/components/UploadDocuments';
import { ProgramContext } from '@src/contexts/ProgramContextProvider';
import { REPLY_TO_INFO_REQUEST } from '@src/graphql/queries/activity';
import { PAYMENT_REQUEST } from '@src/graphql/queries/paymentRequest';
import { useAuth } from '@src/hooks/useAuth';
import { PaymentRequestStatus, RuleType } from '@src/types';
import { isWithinDocumentDeadline } from '@src/utilities/paymentRequestHelper';
import { Messages, MessageType, TrackableButton, useLocale } from 'pe-core-frontend';
import { useContext, useState } from 'react';
import styles from './DocumentSubmission.module.scss';
export const DocumentSubmission = ({ paymentRequest, program }) => {
    const { t } = useLocale();
    const { currentUser } = useAuth();
    const { getPolicy } = useContext(ProgramContext);
    const [docs, setDocs] = useState([]);
    const [submitReply] = useMutation(REPLY_TO_INFO_REQUEST, {
        refetchQueries: [{ query: PAYMENT_REQUEST, variables: { id: paymentRequest.id } }],
    });
    const [fieldErrors, setFieldErrors] = useState({});
    const [messages, setMessages] = useState([]);
    const isCurrentUser = currentUser?.employeeId === program.employeeId;
    if (!isCurrentUser || paymentRequest.status !== PaymentRequestStatus.ACTIVE)
        return null;
    let canSubmitDocs = paymentRequest?.status === PaymentRequestStatus.ACTIVE && // user can submit documents only if the claim is active
        currentUser?.employeeId === program.employeeId; // user can submit documents only if they are the claimant
    let docSubmissionMessage;
    if (canSubmitDocs && paymentRequest) {
        const approvalPolicy = paymentRequest?.program && getPolicy(RuleType.APPROVAL_CHAIN, paymentRequest?.program);
        if (approvalPolicy) {
            const { success, message } = isWithinDocumentDeadline(paymentRequest, approvalPolicy, t);
            canSubmitDocs = success;
            docSubmissionMessage = message;
        }
    }
    const handleSubmit = async () => {
        setFieldErrors({});
        if (docs.length === 0) {
            setFieldErrors({ docs: t('Please upload at least one document') });
            return;
        }
        try {
            const result = await submitReply({
                variables: {
                    input: {
                        id: paymentRequest.id,
                        type: 'payment_requests',
                        message: '',
                        docs: { docs },
                    },
                },
            });
            if (result.data?.replyToInfoRequest) {
                setMessages([{ type: MessageType.SUCCESS, value: t('Document submitted successfully') }]);
                return;
            }
            setMessages([{ type: MessageType.ERROR, value: t('Unable to submit') }]);
        }
        catch (err) {
            setMessages([{ type: MessageType.ERROR, value: t('Unable to submit') }]);
        }
    };
    if (!canSubmitDocs) {
        return (_jsx("div", { children: _jsx(Alert, { display: true, type: 'info', hideClose: true, className: 'mt-4', children: docSubmissionMessage }) }));
    }
    return (_jsx(Card, { className: styles.container, children: _jsxs(CardContent, { className: styles.content, children: [_jsx(Typography, { variant: 'h3', children: t('Document submission') }), _jsx(Messages, { messages: messages }), _jsx(UploadDocuments, { title: t('Upload document'), documents: docs, onChange: docs => {
                        setDocs(docs);
                        setFieldErrors({});
                    }, errors: fieldErrors.docs, infoText: '' }), _jsx("div", { className: styles.actions, children: _jsx(TrackableButton, { enText: 'Submit documents for review', tag: 'document_cta', appearance: 'primary', onClick: handleSubmit, children: t('Submit documents for review') }) })] }) }));
};
